import { Button } from '@/ui';
import {
  ACAO_STATUS_ICON,
  ACAO_STATUS_LABEL,
  contarAcoesPorStatus,
  isPlanoEditavel,
  PLANO_STATUS_LABEL,
  type AcaoStatus,
  type Plano,
  type PlanoStatus,
} from '@/domain/planos';
import { rotuloTarpDeOrigem } from '@/domain/planos/mpa';
import { formatarDiaBr, formatarDiaDoInstante, formatarInstanteBrasilia } from '@/domain/fuso-brasilia';

const STATUS_COLOR: Record<PlanoStatus, string> = {
  pendente: '#f97316',
  em_andamento: '#78B4CF',
  concluido: '#22c55e',
  extinto: '#94a3b8',
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-jf-dim">{label}</div>
      <div className="text-sm text-white">{value}</div>
    </div>
  );
}

/**
 * Detalhe do plano de ação (GUPM-187): identificação + lista de ações. RT/admin
 * avança o status de cada ação (Iniciar/Concluir); operador vê read-only (RLS é o gate real).
 */
export function PlanoDetail({
  plano,
  canEdit,
  updating,
  onBack,
  onUpdateAcao,
}: {
  plano: Plano;
  canEdit: boolean;
  updating: boolean;
  onBack: () => void;
  onUpdateAcao: (acaoId: string, status: AcaoStatus) => void;
}) {
  const c = contarAcoesPorStatus(plano.acoes);
  const sc = STATUS_COLOR[plano.status];
  const editable = canEdit && isPlanoEditavel(plano.status);
  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="text-xs font-bold text-jf-muted hover:text-white">
        ← Voltar
      </button>

      <div className="rounded-xl border border-jf-border bg-jf-surface p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-base font-bold text-white">{plano.codigo}</span>
          <span
            className="rounded-full border px-2.5 py-0.5 text-[11px] font-bold"
            style={{ color: sc, borderColor: `${sc}66`, background: `${sc}1a` }}
          >
            {PLANO_STATUS_LABEL[plano.status]}
          </span>
        </div>
        <h2 className="mt-1 text-sm font-semibold text-white">{plano.titulo}</h2>

        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-jf-border pt-3">
          <Field
            label="TARP de origem"
            value={[
              rotuloTarpDeOrigem({ tarpRecordId: plano.tarpRecordId, tarpSeq: plano.tarpSeq }),
              plano.tarpCode,
            ].filter(Boolean).join(' · ')}
          />
          <Field label="Barragem" value={plano.damCode ? `Barragem ${plano.damCode}` : 'Ainda não identificada'} />
          <Field label="Nível" value={plano.nivel ?? '—'} />
          <Field label="Modo / motivo" value={plano.modo ?? '—'} />
          <Field label="Aberto em" value={formatarInstanteBrasilia(plano.createdAt)} />
        </div>
      </div>

      <div className="rounded-xl border border-jf-border bg-jf-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Ações</h3>
          <span className="text-xs text-jf-muted">
            {c.concluido}/{c.total} concluídas
          </span>
        </div>

        {plano.acoes.length === 0 ? (
          <p className="text-xs text-jf-muted">Nenhuma ação cadastrada neste plano.</p>
        ) : (
          <ul className="space-y-2">
            {plano.acoes.map((a) => (
              <li key={a.id} className="rounded-lg border border-jf-border bg-jf-surface2/40 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] text-slate-200">{a.descricao}</p>
                  <span className="shrink-0 text-xs font-bold text-jf-muted">
                    {ACAO_STATUS_ICON[a.status]} {ACAO_STATUS_LABEL[a.status]}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-jf-dim">
                  <span>👤 {a.responsavel}</span>
                  {a.prazo && <span>📅 Prazo {formatarDiaBr(a.prazo)}</span>}
                  {a.status === 'concluido' && (
                    <span className="text-level-normal">
                      {a.concluidoEm ? `Concluída em ${formatarDiaDoInstante(a.concluidoEm)}` : 'Conclusão sem data registrada'}
                    </span>
                  )}
                </div>
                {editable && a.status !== 'concluido' && (
                  <div className="mt-2 flex gap-2">
                    {a.status === 'pendente' && (
                      <Button variant="ghost" onClick={() => onUpdateAcao(a.id, 'andamento')} disabled={updating}>
                        Iniciar
                      </Button>
                    )}
                    <Button variant="green" onClick={() => onUpdateAcao(a.id, 'concluido')} disabled={updating}>
                      Concluir
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
